import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { useAuth } from "react-oidc-context";
import { useTranslation } from "react-i18next";
import usageNudgeApi, {
  UsageNudgeBatchPreviewDto,
  UsageNudgeBatchStatusDto,
} from "../../../api/analytics/usageNudgeApi";
import AnalyticsFilterInputs from "../../../types/analytics/AnalyticsFilterInputs";
import { UsageNudgeAudience } from "../../../types/nudges/NudgeDto";
import { ModalPopup, Tooltip } from "../../common";
import SmallLoader from "../../loaders/SmallLoader";

const BATCH_POLL_INTERVAL_MS = 10_000;

export function getActiveBatchStorageKey(audience: UsageNudgeAudience) {
  return `usageNudgeActiveBatchId:${audience}`;
}

interface UsageNudgeBatchComposeModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  filters: AnalyticsFilterInputs | null;
  recipientEmployeeIds: number[];
  audience: UsageNudgeAudience;
  onSent?: (recipientEmployeeIds: number[]) => void;
  onCompleted?: (status: UsageNudgeBatchStatusDto) => void;
  onActiveBatchChange?: (batchId: number | null) => void;
  /** Parent in-flight batch (banner). When set, reopen resumes that send. */
  resumeBatchId?: number | null;
}

function isBatchTerminal(status: UsageNudgeBatchStatusDto["sendStatus"]) {
  return status === "SENT" || status === "FAILED";
}

function normalizeOutcome(outcome: string) {
  return outcome.replace(/-/g, "").toUpperCase();
}

function UsageNudgeBatchComposeModal({
  isOpen,
  onOpenChange,
  filters,
  recipientEmployeeIds,
  audience,
  onSent,
  onCompleted,
  onActiveBatchChange,
  resumeBatchId = null,
}: UsageNudgeBatchComposeModalProps) {
  const { t } = useTranslation();
  const auth = useAuth();
  const api = useMemo(
    () => new usageNudgeApi(auth.user?.access_token),
    [auth.user?.access_token],
  );
  const [preview, setPreview] = useState<UsageNudgeBatchPreviewDto | null>(
    null,
  );
  const [batchStatus, setBatchStatus] =
    useState<UsageNudgeBatchStatusDto | null>(null);
  const [activeBatchId, setActiveBatchId] = useState<number | null>(null);
  const [adminMessage, setAdminMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const notifiedSentRef = useRef(false);
  const showingResultsRef = useRef(false);
  const recipientKey = recipientEmployeeIds.join(",");
  const storageKey = getActiveBatchStorageKey(audience);

  const resetComposeState = useCallback(() => {
    setPreview(null);
    setBatchStatus(null);
    setAdminMessage("");
    setHasError(false);
    setErrorMessage(null);
    setIsLoading(false);
    setIsSending(false);
    notifiedSentRef.current = false;
    showingResultsRef.current = false;
  }, []);

  const updateActiveBatchId = useCallback(
    (batchId: number | null) => {
      setActiveBatchId(batchId);
      onActiveBatchChange?.(batchId);
      if (batchId) {
        sessionStorage.setItem(storageKey, String(batchId));
      } else {
        sessionStorage.removeItem(storageKey);
      }
    },
    [onActiveBatchChange, storageKey],
  );

  const handleClose = useCallback(() => {
    // Finished batches are no longer resumable - clear results so the next
    // open always loads a fresh preview for the new selection.
    if (!activeBatchId) {
      resetComposeState();
      sessionStorage.removeItem(storageKey);
    }
    onOpenChange(false);
  }, [activeBatchId, onOpenChange, resetComposeState, storageKey]);

  useEffect(() => {
    if (!isOpen) {
      // Keep in-progress batches resumable; wipe idle/completed UI state.
      if (!activeBatchId) {
        resetComposeState();
      }
      return;
    }

    // Already tracking an in-flight send inside this modal instance.
    if (activeBatchId) return;

    const recipientIds = recipientKey
      .split(",")
      .map(Number)
      .filter((id) => id > 0);

    let isCurrent = true;

    const loadPreview = () => {
      if (!filters || recipientIds.length === 0) return;

      // Clear any previous send results before loading the new preview.
      setBatchStatus(null);
      setPreview(null);
      setAdminMessage("");
      notifiedSentRef.current = false;
      setIsLoading(true);
      setHasError(false);
      setErrorMessage(null);

      api
        .previewBatch(filters, recipientIds, audience)
        .then((response) => {
          if (!isCurrent) return;
          if (response.status === "OK") {
            setPreview(response.preview);
            return;
          }
          if (response.status === "BATCH_LIMIT_EXCEEDED") {
            setErrorMessage(
              t("Analytics.Widgets.Usage.Nudge.BatchLimitExceeded", {
                defaultValue:
                  "You can nudge up to {{max}} people at once. Narrow your selection and try again.",
                max: response.maxRecipients,
              }),
            );
          } else {
            setErrorMessage(
              t(
                "Analytics.Widgets.Usage.Nudge.PreviewError",
                "Could not load nudge details. Try again.",
              ),
            );
          }
          setHasError(true);
        })
        .catch(() => {
          if (!isCurrent) return;
          setHasError(true);
          setErrorMessage(
            t(
              "Analytics.Widgets.Usage.Nudge.PreviewError",
              "Could not load nudge details. Try again.",
            ),
          );
        })
        .finally(() => isCurrent && setIsLoading(false));
    };

    // Keep the completed-results screen until the user closes the modal.
    // Check this before resumeBatchId - parent prop can lag one render behind
    // when the batch just completed, which would flip isLoading back on.
    if (showingResultsRef.current) {
      setIsLoading(false);
      return;
    }

    // Resume only when the parent still has an in-flight batch (banner).
    // Verify it is not already finished - a completed id can linger in
    // sessionStorage/parent state and would otherwise block a new preview.
    if (resumeBatchId && resumeBatchId > 0) {
      setIsLoading(true);
      api
        .getBatchStatus(resumeBatchId)
        .then((response) => {
          if (!isCurrent) return;

          if (
            response.status === "OK" &&
            !isBatchTerminal(response.batch.sendStatus)
          ) {
            setBatchStatus(response.batch);
            updateActiveBatchId(resumeBatchId);
            setIsLoading(false);
            return;
          }

          // Stale completed/missing batch - clear and start a fresh preview.
          sessionStorage.removeItem(storageKey);
          onActiveBatchChange?.(null);
          showingResultsRef.current = false;
          loadPreview();
        })
        .catch(() => {
          if (!isCurrent) return;
          sessionStorage.removeItem(storageKey);
          onActiveBatchChange?.(null);
          showingResultsRef.current = false;
          loadPreview();
        });

      return () => {
        isCurrent = false;
      };
    }

    // New selection compose: drop any stale stored batch id and preview.
    sessionStorage.removeItem(storageKey);
    loadPreview();

    return () => {
      isCurrent = false;
    };
  }, [
    activeBatchId,
    api,
    audience,
    filters,
    isOpen,
    onActiveBatchChange,
    recipientKey,
    resetComposeState,
    resumeBatchId,
    storageKey,
    t,
    updateActiveBatchId,
  ]);

  useEffect(() => {
    if (!isOpen || !activeBatchId) return;

    let isCurrent = true;
    let timeoutId: number | undefined;

    const poll = async () => {
      const response = await api.getBatchStatus(activeBatchId);
      if (!isCurrent) return;

      if (response.status === "OK") {
        setBatchStatus(response.batch);
        if (isBatchTerminal(response.batch.sendStatus)) {
          showingResultsRef.current = true;
          setIsLoading(false);
          updateActiveBatchId(null);
          if (!notifiedSentRef.current) {
            notifiedSentRef.current = true;
            const sentIds = response.batch.recipients
              .filter((r) => normalizeOutcome(r.outcome) === "SENT")
              .map((r) => r.recipientEmployeeId);
            onSent?.(sentIds);
            onCompleted?.(response.batch);
          }
          return;
        }
      }

      timeoutId = window.setTimeout(poll, BATCH_POLL_INTERVAL_MS);
    };

    poll();

    return () => {
      isCurrent = false;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [activeBatchId, api, isOpen, onCompleted, onSent, updateActiveBatchId]);

  const handleSend = async () => {
    if (!filters || !preview?.summary || isSending || activeBatchId) return;
    if (preview.summary.eligibleCount <= 0) return;

    setIsSending(true);
    setHasError(false);
    setErrorMessage(null);

    const response = await api.enqueueBatch(
      filters,
      recipientEmployeeIds,
      audience,
      adminMessage,
    );
    setIsSending(false);

    if (response.status === "QUEUED") {
      notifiedSentRef.current = false;
      updateActiveBatchId(response.result.batchId);
      setBatchStatus(null);
      return;
    }

    if (response.status === "BATCH_LIMIT_EXCEEDED") {
      setErrorMessage(
        t("Analytics.Widgets.Usage.Nudge.BatchLimitExceeded", {
          defaultValue:
            "You can nudge up to {{max}} people at once. Narrow your selection and try again.",
          max: response.maxRecipients,
        }),
      );
    } else {
      setErrorMessage(
        t(
          "Analytics.Widgets.Usage.Nudge.BatchError",
          "The selected nudges could not be sent. Try again.",
        ),
      );
    }
    setHasError(true);
  };

  const title =
    audience === "MANAGER"
      ? t(
          "Analytics.Widgets.Usage.Nudge.BatchComposeTitleManager",
          "Nudge selected managers",
        )
      : t(
          "Analytics.Widgets.Usage.Nudge.BatchComposeTitleEmployee",
          "Nudge selected employees",
        );

  const summary = preview?.summary;
  const isPolling = Boolean(activeBatchId);
  const isComplete =
    batchStatus != null && isBatchTerminal(batchStatus.sendStatus);
  const isInProgress =
    batchStatus != null &&
    (batchStatus.sendStatus === "IN-PROGRESS" ||
      batchStatus.sendStatus === "READY");

  const showResults = isComplete && batchStatus;

  const primaryLabel = showResults
    ? t("Analytics.Widgets.Usage.Nudge.Close", "Close")
    : isSending
      ? t("Analytics.Widgets.Usage.Nudge.Sending", "Sending...")
      : isPolling
        ? t(
            "Analytics.Widgets.Usage.Nudge.BatchInProgress",
            "Sending in background...",
          )
        : t("Analytics.Widgets.Usage.Nudge.SendAction", "Send");

  const progressPercent =
    batchStatus && batchStatus.recipientCount > 0
      ? Math.round(
          ((batchStatus.sentCount +
            batchStatus.skippedCount +
            batchStatus.failedCount) /
            batchStatus.recipientCount) *
            100,
        )
      : 0;

  return (
    <ModalPopup
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleClose();
          return;
        }
        onOpenChange(true);
      }}
      title={title}
      titleDescription={t(
        "Analytics.Widgets.Usage.Nudge.BatchComposeDescription",
        "Each person receives a personalised notification with the shared message below.",
      )}
      width="NORMAL"
      primaryButtonText={primaryLabel}
      secondaryButtonText={t("Analytics.Widgets.Usage.Nudge.Cancel", "Cancel")}
      onPrimaryButtonClick={showResults ? handleClose : handleSend}
      onSecondaryButtonClick={handleClose}
      isLoading={isSending}
      isPrimaryButtonDisabled={
        showResults
          ? false
          : isLoading ||
            hasError ||
            isSending ||
            isPolling ||
            (summary?.eligibleCount ?? 0) <= 0
      }
    >
      {isLoading && !showResults && (
        <div className="py-6">
          <SmallLoader />
          <p className="pt-2 text-center text-sm text-gray-600">
            {t(
              "Analytics.Widgets.Usage.Nudge.LoadingPreview",
              "Loading nudge details...",
            )}
          </p>
        </div>
      )}

      {!isLoading && hasError && !isPolling && !showResults && (
        <p className="text-sm text-red-600">
          {errorMessage ??
            t(
              "Analytics.Widgets.Usage.Nudge.PreviewError",
              "Could not load nudge details. Try again.",
            )}
        </p>
      )}

      {!isLoading && summary && !isPolling && !showResults && !hasError && (
        <div className="space-y-4 text-sm text-gray-700">
          <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-3">
            <p className="font-semibold text-gray-800">
              {t("Analytics.Widgets.Usage.Nudge.BatchSummaryTitle", "Summary")}
            </p>
            <ul className="mt-2 space-y-1 text-sm">
              <li>
                {t("Analytics.Widgets.Usage.Nudge.BatchSelectedCount", {
                  defaultValue: "{{count}} selected",
                  count: summary.selectedCount,
                })}
              </li>
              <li>
                {t("Analytics.Widgets.Usage.Nudge.BatchEligibleCount", {
                  defaultValue: "{{count}} eligible to send now",
                  count: summary.eligibleCount,
                })}
              </li>
              {summary.cooldownCount > 0 && (
                <li className="flex items-center gap-1.5">
                  <span>
                    {t("Analytics.Widgets.Usage.Nudge.BatchCooldownCount", {
                      defaultValue: "{{count}} on cooldown",
                      count: summary.cooldownCount,
                    })}
                  </span>
                  <Tooltip
                    content={t(
                      "Analytics.Widgets.Usage.Nudge.BatchCooldownCountInfo",
                      "These people still have outstanding journeys, but were nudged recently and cannot be nudged again until 72 hours have passed.",
                    )}
                    triggerElement={
                      <button
                        type="button"
                        className="text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                        aria-label={t(
                          "Analytics.Widgets.Usage.Nudge.BatchCooldownCountInfoLabel",
                          "About cooldown",
                        )}
                      >
                        <FontAwesomeIcon icon={faInfoCircle} aria-hidden />
                      </button>
                    }
                  />
                </li>
              )}
              {summary.notNudgeableCount > 0 && (
                <li className="flex items-center gap-1.5">
                  <span>
                    {t("Analytics.Widgets.Usage.Nudge.BatchNotNudgeableCount", {
                      defaultValue: "{{count}} with nothing outstanding",
                      count: summary.notNudgeableCount,
                    })}
                  </span>
                  <Tooltip
                    content={t(
                      "Analytics.Widgets.Usage.Nudge.BatchNotNudgeableCountInfo",
                      "These people have no incomplete journeys in the current selection, so there is nothing to nudge them about.",
                    )}
                    triggerElement={
                      <button
                        type="button"
                        className="text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                        aria-label={t(
                          "Analytics.Widgets.Usage.Nudge.BatchNotNudgeableCountInfoLabel",
                          "About nothing outstanding",
                        )}
                      >
                        <FontAwesomeIcon icon={faInfoCircle} aria-hidden />
                      </button>
                    }
                  />
                </li>
              )}
            </ul>
          </div>

          {summary.sample.length > 0 && (
            <div>
              <p className="mb-2 font-semibold text-gray-800">
                {t("Analytics.Widgets.Usage.Nudge.BatchSampleTitle", {
                  defaultValue: "Sample ({{count}} of {{total}})",
                  count: summary.sample.length,
                  total: summary.selectedCount,
                })}
              </p>
              <div className="max-h-40 overflow-y-auto rounded-md border border-gray-200">
                <ul className="divide-y divide-gray-200">
                  {summary.sample.map((recipient) => (
                    <li
                      key={recipient.recipientEmployeeId}
                      className="flex items-center justify-between gap-4 px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-gray-800">
                          {recipient.recipientName}
                        </p>
                        <p className="text-xs text-gray-600">
                          {t(
                            "Analytics.Widgets.Usage.Nudge.BatchRecipientOutstanding",
                            {
                              defaultValue: "{{count}} outstanding",
                              count: recipient.outstandingCount,
                            },
                          )}
                        </p>
                      </div>
                      {!recipient.canSend && (
                        <span className="shrink-0 text-xs font-semibold text-amber-700">
                          {recipient.cooldownEndsAt
                            ? t(
                                "Analytics.Widgets.Usage.Nudge.CooldownShort",
                                "Recently sent",
                              )
                            : t(
                                "Analytics.Widgets.Usage.Nudge.NotNudgeable",
                                "Nothing outstanding",
                              )}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <label className="block">
            <span className="mb-1 block font-semibold text-gray-800">
              {t(
                "Analytics.Widgets.Usage.Nudge.SharedMessageLabel",
                "Shared optional message",
              )}
            </span>
            <textarea
              value={adminMessage}
              onChange={(event) => setAdminMessage(event.target.value)}
              rows={4}
              maxLength={4000}
              placeholder={t(
                "Analytics.Widgets.Usage.Nudge.MessagePlaceholder",
                "Add a short note for the recipient (optional)",
              )}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-800 placeholder:not-italic focus:border-gray-300 focus:outline-none focus:ring-0"
            />
          </label>
        </div>
      )}

      {isPolling && (
        <div className="space-y-4 text-sm text-gray-700">
          <div className="py-4">
            <SmallLoader />
            <p className="pt-2 text-center text-sm text-gray-600">
              {isInProgress || !batchStatus
                ? t(
                    "Analytics.Widgets.Usage.Nudge.BatchProgress",
                    "Sending nudges in the background. You can close this dialog and check back later.",
                  )
                : t(
                    "Analytics.Widgets.Usage.Nudge.BatchLoadingStatus",
                    "Loading batch status...",
                  )}
            </p>
          </div>
          {batchStatus && (
            <div className="space-y-2">
              <div className="h-2 overflow-hidden rounded-full bg-gray-200">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <p className="text-center text-xs text-gray-600">
                {t("Analytics.Widgets.Usage.Nudge.BatchProgressCounts", {
                  defaultValue:
                    "{{sent}} sent · {{skipped}} skipped · {{failed}} failed · {{pending}} pending",
                  sent: batchStatus.sentCount,
                  skipped: batchStatus.skippedCount,
                  failed: batchStatus.failedCount,
                  pending: batchStatus.pendingCount,
                })}
              </p>
            </div>
          )}
        </div>
      )}

      {showResults && batchStatus && (
        <div className="space-y-4 text-sm text-gray-700">
          <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-3">
            <p className="font-semibold text-gray-800">
              {t("Analytics.Widgets.Usage.Nudge.BatchResultsTitle", "Results")}
            </p>
            <p className="mt-1 text-sm text-gray-600">
              {t("Analytics.Widgets.Usage.Nudge.BatchQueuedBy", {
                defaultValue: "Queued by {{name}} on {{date}}",
                name: batchStatus.senderName,
                date: new Date(batchStatus.dateCreated).toLocaleString(),
              })}
            </p>
            {batchStatus.sendResultSummary && (
              <p className="mt-1 text-sm text-gray-600">
                {batchStatus.sendResultSummary}
              </p>
            )}
          </div>

          <div className="max-h-64 overflow-y-auto rounded-md border border-gray-200">
            <ul className="divide-y divide-gray-200">
              {batchStatus.recipients.map((recipient) => {
                const outcome = normalizeOutcome(recipient.outcome);
                const isFailed =
                  outcome === "DELIVERYFAILED" || outcome === "FAILED";
                const isSent = outcome === "SENT";
                const isSkipped =
                  outcome === "COOLDOWNACTIVE" || outcome === "NOTNUDGEABLE";

                return (
                  <li
                    key={recipient.recipientEmployeeId}
                    className="px-3 py-2.5"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-gray-800">
                          {recipient.recipientName}
                        </p>
                        {isFailed && recipient.lastErrorDetail && (
                          <p className="mt-1 text-xs text-red-600">
                            {recipient.lastErrorDetail}
                          </p>
                        )}
                      </div>
                      <span
                        className={`shrink-0 text-xs font-semibold ${
                          isSent
                            ? "text-green-700"
                            : isFailed
                              ? "text-red-700"
                              : isSkipped
                                ? "text-amber-700"
                                : "text-gray-600"
                        }`}
                      >
                        {isSent
                          ? t(
                              "Analytics.Widgets.Usage.Nudge.OutcomeSent",
                              "Sent",
                            )
                          : isFailed
                            ? t(
                                "Analytics.Widgets.Usage.Nudge.OutcomeFailed",
                                "Failed",
                              )
                            : outcome === "COOLDOWNACTIVE"
                              ? t(
                                  "Analytics.Widgets.Usage.Nudge.CooldownShort",
                                  "Recently sent",
                                )
                              : t(
                                  "Analytics.Widgets.Usage.Nudge.NotNudgeable",
                                  "Nothing outstanding",
                                )}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </ModalPopup>
  );
}

export default UsageNudgeBatchComposeModal;
